import React, { useEffect, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import {
  Box,
  ButtonIcon,
  Chip,
  Icon,
  InlineMessage,
  MenuInput,
} from '@saviynt/design-system';
import classnames from 'classnames';

import useGetRuleMetadata from '../../hooks/use-getrulesmetadata';
import { type Attribute, type Condition } from '../../services/types';
import { isEmpty } from '../../utils';

import './RuleCondition.css';

const MAX_ALLOWED_VALUES = 25;

interface RuleConditionProps {
  ruleCondition: Condition[];
  setUpdatedCondition: Dispatch<SetStateAction<Condition>>;
  setIsRuleConfirmed: Dispatch<SetStateAction<boolean>>;
}

function RuleCondition({
  // rule,
  ruleCondition,
  setUpdatedCondition,
  setIsRuleConfirmed,
}: RuleConditionProps) {
  const [condition, setCondition] = useState<Condition>(ruleCondition[0]);
  const [inputValue, setInputValue] = useState<string>('');
  const [chips, setChips] = useState<string[]>([]);
  const [selectedAttribute, setSelectedAttribute] = useState<string | null>(
    null
  );
  const [selectedOperator, setSelectedOperator] = useState<string | null>(null);

  const { data } = useGetRuleMetadata();

  const [attributes, setAttributes] = useState<Attribute[]>();

  useEffect(() => {
    if (data?.entity.users) {
      setAttributes(data.entity.users);
    }
  }, [data]);

  useEffect(() => {
    setCondition(ruleCondition[0]);
  }, [ruleCondition]);

  useEffect(() => {
    if (condition) {
      setSelectedAttribute(condition.attribute);
      setSelectedOperator(condition.operator);
      setChips(condition.values);
    }
  }, [condition, attributes]);

  // Handle Attribute selection change
  const handleAttributesChange = (selectedOption: { value: string }) => {
    setUpdatedCondition((prevCondition) => ({
      ...prevCondition,
      ruleDescription: `If users ${selectedOption.value} ${
        prevCondition.operator
      } ${prevCondition.values.join(', ')}`,
      attribute: selectedOption.value,
    }));
    setSelectedAttribute(selectedOption.value);
    setIsRuleConfirmed(false);
  };

  // Handle operator selection change
  const handleOperatorsChange = (selectedOption: { value: string }) => {
    setUpdatedCondition((prevCondition) => ({
      ...prevCondition,
      ruleDescription: `If users ${prevCondition.attribute} ${
        selectedOption.value
      } ${prevCondition.values.join(', ')}`,
      operator: selectedOption.value,
    }));
    setSelectedOperator(selectedOption.value);
    setIsRuleConfirmed(false);
  };

  // Handle input field change
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
    setIsRuleConfirmed(false);
  };

  useEffect(() => {
    setUpdatedCondition((prevCondition) => ({
      ...prevCondition,
      ruleDescription: `If users ${prevCondition.attribute} ${
        prevCondition.operator
      } ${chips.join(', ')}`,
      values: chips,
    }));
  }, [chips, setUpdatedCondition]);

  // Handle keydown event (for adding chips on Enter)
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && inputValue.trim() !== '') {
      // Add chip and reset input field
      setChips((prevChips) => [...prevChips, inputValue.trim()]);
      setInputValue('');
      event.preventDefault();
    }
  };

  // Handle chip removal
  const handleChipRemove = (chip: string) => {
    setChips((prevChips) => prevChips.filter((c) => c !== chip));
    setIsRuleConfirmed(false);
  };

  return (
    <div>
      {data && (
        <Box className='Ext-RuleCondition'>
          <Box className='Ext-RuleCondition-container'>
            <MenuInput
              options={
                attributes?.map((user) => ({
                  label: user.name,
                  value: user.name,
                  isChecked: user.name === selectedAttribute,
                })) ?? []
              }
              onChange={handleAttributesChange}
              placeholder='Select an attribute'
              heightConfigs={{ maxHeight: '10rem', height: '4rem' }}
              menuItemHeightConfigs={{ height: '2rem' }}
              isPlacementLocked
              className={classnames(
                'Ext-RuleCondition-container-attributesDropdown'
              )}
            />
            {!isEmpty(selectedAttribute) && (
              <MenuInput
                options={data.operators.map((operator) => ({
                  label: operator,
                  value: operator,
                  isChecked: operator === selectedOperator,
                }))}
                heightConfigs={{ maxHeight: '10rem' }}
                menuItemHeightConfigs={{ height: '2rem' }}
                isPlacementLocked
                onChange={handleOperatorsChange}
                placeholder='Select a condition'
                className={classnames(
                  'Ext-RuleCondition-container-conditionsDropdown'
                )}
              />
            )}
            {!isEmpty(selectedOperator) && !isEmpty(selectedAttribute) && (
              <Box className='Ext-RuleCondition-container-inputBoxWrapper'>
                <Box
                  className={classnames(
                    'Ext-RuleCondition-container-inputBoxWrapper-inputBox'
                  )}>
                  {chips.map((chip) => (
                    <Box
                      key={chip}
                      className='Ext-RuleCondition-container-inputBoxWrapper-inputBox-chip'>
                      <Chip
                        key={chip}
                        size='medium'
                        label={chip}
                        kind='outlined'
                        rightIcon={
                          <ButtonIcon
                            kind='ghost'
                            size='xxxSmall'
                            icon={
                              <Icon
                                className='Ext-RuleCondition-container-inputBoxWrapper-inputBox-chipDelete'
                                kind='close'
                                size='xxxSmall'
                              />
                            }
                            onClick={() => handleChipRemove(chip)}
                          />
                        }
                      />
                    </Box>
                  ))}
                  <input
                    type='text'
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    placeholder='Type a value and then press enter'
                    className='Ext-RuleCondition-container-inputBoxWrapper-inputField'
                    disabled={chips.length >= MAX_ALLOWED_VALUES}
                  />
                </Box>
                {chips.length >= MAX_ALLOWED_VALUES && (
                  <InlineMessage
                    text='Not more than 25 are allowed'
                    colorTheme='info'
                  />
                )}
              </Box>
            )}
          </Box>
        </Box>
      )}
    </div>
  );
}

export default RuleCondition;
